/**
 * Documentation sections — the single source shared by the sidebar nav (AppShell.tsx)
 * and the /docs content router (DocsPage.tsx). Slugs are the route contract
 * (/docs/:section) and the keys of DocsPage's element map.
 *
 * `parent` makes a section a subpage: the sidebar indents it under that section.
 * It is one level deep, and it does not change the route, which stays /docs/<slug>.
 * A subpage is listed directly after its parent, because the order here is the
 * sidebar order.
 */
export const DOC_SECTIONS = [
  { slug: 'get-started', label: 'Get started', blurb: 'Connect your AI agent' },
  { slug: 'request-access', label: 'Requesting access', blurb: 'Ask for a resource' },
  { slug: 'tasks', label: 'Tasks', blurb: 'One ticket for access and infra work' },
  { slug: 'mcp', label: 'MCP tools', blurb: 'Agent tool reference' },
  { slug: 'agents', label: 'Agents', blurb: 'Node fleet & Atlas IPs' },
  { slug: 'securelink', label: 'SecureLink', blurb: 'What it is and who connects' },
  { slug: 'vpn-subnets', label: 'VPN subnets', blurb: 'Who can reach what', parent: 'securelink' },
  {
    slug: 'connection-audit',
    label: 'Connection audit',
    blurb: 'Who reached which device',
    parent: 'securelink',
  },
  { slug: 'securelink-tcp-relay', label: 'TCP relay', blurb: 'When UDP 443 is blocked', parent: 'securelink' },
  {
    slug: 'securelink-device-provision',
    label: 'Device provisioning',
    blurb: 'How a device gets its peer and its hub',
    parent: 'securelink',
  },
  { slug: 'transforge', label: 'TransForge', blurb: 'What it is and where it runs' },
  {
    slug: 'transforge-multi-cloud',
    label: 'Multi-cloud',
    blurb: 'Two clouds, one global name',
    parent: 'transforge',
  },
  { slug: 'transforge-clients', label: 'Clients', blurb: 'Who calls it and how', parent: 'transforge' },
  {
    slug: 'transforge-operations',
    label: 'Deploy and operate',
    blurb: 'Releases, scaling, open issues',
    parent: 'transforge',
  },
] as const;

export type DocSlug = (typeof DOC_SECTIONS)[number]['slug'];

/** The section a slug sits under in the sidebar, or undefined for a top-level section. */
export function docParent(slug: string | undefined): DocSlug | undefined {
  const section = DOC_SECTIONS.find((s) => s.slug === slug);
  return section && 'parent' in section ? section.parent : undefined;
}

/**
 * Breadcrumb levels below "Docs & MCP" for a docs route: the parent section, if
 * any, then the page. Empty for any route outside /docs and for an unknown slug.
 */
export function docTrail(pathname: string): string[] {
  if (!pathname.startsWith('/docs/')) return [];
  const slug = pathname.split('/')[2];
  const label = (s: string | undefined): string | undefined => DOC_SECTIONS.find((d) => d.slug === s)?.label;
  return [label(docParent(slug)), label(slug)].filter((l) => l !== undefined);
}
